import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ManageStadium.css';
import Button from "../../components/Button/Button";
import {
  useAlertDialog,
  useConfirmDialog,
} from "../../components/ConfirmDialog/ConfirmDialogProvider";
import LoadingOverlay from "../../components/LoadingOverlay/LoadingOverlay";
import Datetime from 'react-datetime';
import 'react-datetime/css/react-datetime.css';

const INITIAL_STADIUM_DATA = {
  name: '',
  address: '',
  capacity: '',
  manager: '',
  phone: '',
  email: '',
  hosts: [],
  price: '',
  slots: [],
  amenities: [],
  type: '',
  surface: '',
};

const INITIAL_SLOT_INPUT = {
  dayOfWeek: 'Monday',
  startTime: '',
  endTime: '',
  startDate: '',
  endDate: '',
  available: true,
  price: '',
};

const normalizeDateValue = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return String(value).slice(0, 10);
  }
  return parsed.toISOString().slice(0, 10);
};

const mapStadiumToFormState = (stadium) => ({
  name: stadium?.name || '',
  address: stadium?.address || '',
  capacity: stadium?.capacity != null ? String(stadium.capacity) : '',
  manager: stadium?.manager || '',
  phone: stadium?.phone || '',
  email: stadium?.email || '',
  hosts: Array.isArray(stadium?.hosts) ? stadium.hosts.map((host) => String(host?._id)) : [],
  price: '',
  slots: Array.isArray(stadium?.slots)
    ? stadium.slots.map((slot) => ({
        dayOfWeek: slot?.dayOfWeek || 'Monday',
        startTime: slot?.startTime || '',
        endTime: slot?.endTime || '',
        startDate: normalizeDateValue(slot?.startDate),
        endDate: normalizeDateValue(slot?.endDate),
        available: slot?.available !== false,
        price: slot?.price != null ? Number(slot.price) : 0,
      }))
    : [],
  amenities: Array.isArray(stadium?.amenities) ? stadium.amenities : [],
  type: stadium?.type || '',
  surface: stadium?.surface || '',
});

function ManageStadium() {
  const showAlert = useAlertDialog();
  const confirm = useConfirmDialog();
  const [stadiumData, setStadiumData] = useState(INITIAL_STADIUM_DATA);
  const [stadiumPictureFile, setStadiumPictureFile] = useState(null);
  const [stadiumMapFile, setStadiumMapFile] = useState(null);
  const [slotInput, setSlotInput] = useState(INITIAL_SLOT_INPUT);
  const [step, setStep] = useState(1);
  const [availableHosts, setAvailableHosts] = useState([]);
  const [existingStadiums, setExistingStadiums] = useState([]);
  const [mode, setMode] = useState('create');
  const [selectedStadiumId, setSelectedStadiumId] = useState('');
  const [loadingStadiums, setLoadingStadiums] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingStadium, setIsDeletingStadium] = useState(false);
  const [view, setView] = useState('list');

  const [availableAmenities] = useState(['Restrooms', 'Locker Rooms', 'Parking']);
  const [availableTypes] = useState(['Indoor', 'Outdoor']);
  const [availableSurfaces] = useState(['Grass', 'Artificial Turf']);
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const resetFormState = () => {
    setStadiumData(INITIAL_STADIUM_DATA);
    setStadiumPictureFile(null);
    setStadiumMapFile(null);
    setSlotInput(INITIAL_SLOT_INPUT);
  };

  const loadStadiums = async () => {
    setLoadingStadiums(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/stadiums`);
      const list = Array.isArray(response.data) ? response.data : [];
      setExistingStadiums(list);
      return list;
    } catch (error) {
      console.error('Error fetching stadiums:', error);
      return [];
    } finally {
      setLoadingStadiums(false);
    }
  };

  useEffect(() => {
    async function fetchHosts() {
      try {
        const token = localStorage.getItem('idToken');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/admin/hosts`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setAvailableHosts(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching hosts:', error);
      }
    }

    fetchHosts();
    loadStadiums();
  }, []);

  const openCreateForm = () => {
    setMode('create');
    setSelectedStadiumId('');
    setStep(1);
    resetFormState();
    setView('form');
  };

  const openEditForm = (stadiumId) => {
    const selected = existingStadiums.find(
      (stadium) => String(stadium?._id) === String(stadiumId)
    );

    if (!selected) return;

    setMode('edit');
    setSelectedStadiumId(String(selected._id));
    setStep(1);
    setStadiumPictureFile(null);
    setStadiumMapFile(null);
    setSlotInput(INITIAL_SLOT_INPUT);
    setStadiumData(mapStadiumToFormState(selected));
    setView('form');
  };

  const handleBackToList = async () => {
    setStep(1);
    setMode('create');
    setSelectedStadiumId('');
    resetFormState();
    await loadStadiums();
    setView('list');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStadiumData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSlotChange = (e) => {
    const { name, value } = e.target;
    setSlotInput((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSlot = () => {
    const { dayOfWeek, startTime, endTime, startDate, endDate, available, price } = slotInput;
    if (!dayOfWeek || !startTime || !endTime || !startDate || !endDate) {
      return;
    }

    setStadiumData((prevData) => ({
      ...prevData,
      slots: [
        ...prevData.slots,
        {
          dayOfWeek,
          startTime,
          endTime,
          startDate,
          endDate,
          available,
          price: parseFloat(price) || parseFloat(prevData.price) || 0,
        },
      ],
    }));

    setSlotInput(INITIAL_SLOT_INPUT);
  };

  const handleAmenitiesChange = (e) => {
    const { value, checked } = e.target;
    setStadiumData((prevData) => {
      const currentAmenities = Array.isArray(prevData.amenities) ? prevData.amenities : [];
      const updatedAmenities = checked
        ? [...currentAmenities, value]
        : currentAmenities.filter((item) => item !== value);
      return { ...prevData, amenities: updatedAmenities };
    });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files && files[0] ? files[0] : null;

    if (name === 'stadiumPicture') {
      setStadiumPictureFile(file);
    }
    if (name === 'stadiumMap') {
      setStadiumMapFile(file);
    }
  };

  const buildFormData = () => {
    const normalizedSlots = stadiumData.slots.map((slot) => ({
      dayOfWeek: slot.dayOfWeek,
      startTime: slot.startTime,
      endTime: slot.endTime,
      startDate: slot.startDate,
      endDate: slot.endDate,
      available: slot.available !== false,
      price: slot.price ? parseFloat(slot.price) : 0,
    }));

    const formData = new FormData();
    formData.append('name', stadiumData.name);
    formData.append('address', stadiumData.address);
    formData.append('capacity', stadiumData.capacity ? String(parseInt(stadiumData.capacity, 10)) : '0');
    formData.append('manager', stadiumData.manager);
    formData.append('phone', stadiumData.phone);
    formData.append('email', stadiumData.email);
    formData.append('hosts', JSON.stringify(stadiumData.hosts || []));
    formData.append('slots', JSON.stringify(normalizedSlots));
    formData.append('amenities', JSON.stringify(stadiumData.amenities || []));
    formData.append('type', stadiumData.type || '');
    formData.append('surface', stadiumData.surface || '');

    if (stadiumPictureFile) {
      formData.append('stadiumPicture', stadiumPictureFile);
    }
    if (stadiumMapFile) {
      formData.append('stadiumMap', stadiumMapFile);
    }

    return formData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      await showAlert({
        title: 'STADIUM',
        message: 'You must be logged in to perform this action.',
      });
      return;
    }

    if (mode === 'edit' && !selectedStadiumId) {
      await showAlert({
        title: 'STADIUM',
        message: 'Please select a stadium to update.',
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = buildFormData();
      const requestConfig = {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      };

      if (mode === 'edit') {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/stadiums/${selectedStadiumId}`,
          formData,
          requestConfig
        );
        await showAlert({
          title: 'STADIUM',
          message: 'Stadium updated successfully!',
        });
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/stadiums/create`,
          formData,
          requestConfig
        );
        await showAlert({
          title: 'STADIUM',
          message: 'Stadium created successfully!',
        });
      }

      await loadStadiums();
      setStep(1);
      setSelectedStadiumId('');
      setMode('create');
      resetFormState();
      setView('list');
    } catch (error) {
      console.error(mode === 'edit' ? 'Error updating stadium:' : 'Error creating stadium:', error);
      if (error.response && error.response.data) {
        await showAlert({
          title: 'STADIUM',
          message: `${mode === 'edit' ? 'Failed to update stadium' : 'Failed to create stadium'}: ${
            error.response.data.message || 'Please check the data and try again.'
          }`,
        });
      } else {
        await showAlert({
          title: 'STADIUM',
          message:
            mode === 'edit'
              ? 'Failed to update stadium. Please try again.'
              : 'Failed to create stadium. Please try again.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteStadium = async () => {
    if (isDeletingStadium) {
      return;
    }

    if (!selectedStadiumId) {
      await showAlert({
        title: 'STADIUM',
        message: 'Please select a stadium to delete.',
      });
      return;
    }

    const shouldDelete = await confirm({
      title: 'DELETE STADIUM',
      message: 'Delete this stadium? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
    });
    if (!shouldDelete) return;

    const idToken = localStorage.getItem('idToken');
    if (!idToken) {
      await showAlert({
        title: 'STADIUM',
        message: 'You must be logged in to perform this action.',
      });
      return;
    }

    try {
      setIsDeletingStadium(true);
      await axios.delete(`${process.env.REACT_APP_API_URL}/stadiums/${selectedStadiumId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      await showAlert({
        title: 'STADIUM',
        message: 'Stadium deleted successfully!',
      });
      await loadStadiums();
      setSelectedStadiumId('');
      resetFormState();
      setStep(1);
      setMode('create');
      setView('list');
    } catch (error) {
      console.error('Error deleting stadium:', error);
      if (error.response && error.response.data) {
        await showAlert({
          title: 'STADIUM',
          message: `Failed to delete stadium: ${error.response.data.message || 'Please try again.'}`,
        });
      } else {
        await showAlert({
          title: 'STADIUM',
          message: 'Failed to delete stadium. Please try again.',
        });
      }
    } finally {
      setIsDeletingStadium(false);
    }
  };

  const renderStadiumList = () => (
    <>
      <div className="stadium-list-toolbar">
        <Button
          text="Create"
          onClick={openCreateForm}
          disabled={loadingStadiums || isSubmitting || isDeletingStadium}
        />
        <Button
          text="Refresh"
          onClick={loadStadiums}
          disabled={loadingStadiums || isSubmitting || isDeletingStadium}
        />
      </div>

      {loadingStadiums && <p className="stadium-list-status">Loading stadiums...</p>}

      {!loadingStadiums && existingStadiums.length === 0 && (
        <p className="stadium-list-status">No stadiums found. Add a new one to get started.</p>
      )}

      <div className="stadium-cards-grid">
        {existingStadiums.map((stadium) => (
          <button
            key={stadium._id}
            type="button"
            className="stadium-card"
            onClick={() => openEditForm(stadium._id)}
          >
            <span className="stadium-card-name">{stadium.name}</span>
            <span className="stadium-card-address">{stadium.address}</span>
            <span className="stadium-card-meta">
              {Array.isArray(stadium.slots) ? stadium.slots.length : 0} slots
            </span>
          </button>
        ))}
      </div>
    </>
  );

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <div className="form-group">
              <label>Name of The Pitch:</label>
              <input type="text" name="name" value={stadiumData.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Address:</label>
              <input type="text" name="address" value={stadiumData.address} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Capacity:</label>
              <input type="number" name="capacity" value={stadiumData.capacity} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Manager:</label>
              <input type="text" name="manager" value={stadiumData.manager} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label>Phone:</label>
              <input type="text" name="phone" value={stadiumData.phone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Email:</label>
              <input type="email" name="email" value={stadiumData.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Pitch Picture:</label>
              <input
                type="file"
                name="stadiumPicture"
                accept="image/*"
                onChange={handleFileChange}
              />
              {stadiumPictureFile && <small>{stadiumPictureFile.name}</small>}
            </div>
            <div className="form-group">
              <label>Pitch Map:</label>
              <input
                type="file"
                name="stadiumMap"
                accept="image/*"
                onChange={handleFileChange}
              />
              {stadiumMapFile && <small>{stadiumMapFile.name}</small>}
            </div>
            <div className="form-group">
              <label>Hosts:</label>
              <fieldset className="host-list">
                {availableHosts.length > 0 ? (
                  availableHosts.map((host) => (
                    <label key={host._id} className="host-item">
                      <input
                        type="checkbox"
                        name="hosts"
                        value={host._id}
                        checked={stadiumData.hosts.includes(String(host._id))}
                        onChange={(e) => {
                          const { value, checked } = e.target;
                          setStadiumData((prevData) => ({
                            ...prevData,
                            hosts: checked
                              ? Array.from(new Set([...prevData.hosts, value]))
                              : prevData.hosts.filter((id) => id !== value),
                          }));
                        }}
                      />
                      {`${host.first_name} ${host.last_name}`}
                    </label>
                  ))
                ) : (
                  <p>No hosts available.</p>
                )}
              </fieldset>
            </div>
          </>
        );
      case 2:
        return (
          <>
            <div className="form-group">
              <label>Day of the Week:</label>
              <select name="dayOfWeek" value={slotInput.dayOfWeek} onChange={handleSlotChange}>
                {daysOfWeek.map((day) => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Start Time:</label>
              <input type="time" name="startTime" value={slotInput.startTime} onChange={handleSlotChange} required />
            </div>
            <div className="form-group">
              <label>End Time:</label>
              <input type="time" name="endTime" value={slotInput.endTime} onChange={handleSlotChange} required />
            </div>
            <div className="form-group">
              <label>Slot Price:</label>
              <input type="number" name="price" value={slotInput.price} onChange={handleSlotChange} placeholder="Enter slot-specific price" />
            </div>
            <div className="form-group">
              <label>Start Date:</label>
              <Datetime
                dateFormat="YYYY-MM-DD"
                timeFormat={false}
                value={slotInput.startDate}
                onChange={(date) =>
                  setSlotInput({
                    ...slotInput,
                    startDate: typeof date === 'string' ? date : date.format("YYYY-MM-DD"),
                  })
                }
              />
            </div>
            <div className="form-group">
              <label>End Date:</label>
              <Datetime
                dateFormat="YYYY-MM-DD"
                timeFormat={false}
                value={slotInput.endDate}
                onChange={(date) =>
                  setSlotInput({
                    ...slotInput,
                    endDate: typeof date === 'string' ? date : date.format("YYYY-MM-DD"),
                  })
                }
              />
            </div>
            <Button
              text="Add Slot"
              onClick={handleAddSlot}
              disabled={loadingStadiums || isSubmitting || isDeletingStadium}
            />
            <div className="form-group">
              <h3>Added Slots:</h3>
              <ul>
                {stadiumData.slots.map((slot, index) => (
                  <li key={index}>
                    {`${slot.dayOfWeek} ${slot.startTime} - ${slot.endTime} (${slot.startDate} to ${slot.endDate}) - ${slot.available ? 'Available' : 'Unavailable'} - Price: ${slot.price}`}
                  </li>
                ))}
              </ul>
            </div>
          </>
        );
      case 3:
        return (
          <>
            <fieldset className="form-group amenities-box">
              <legend>Amenities:</legend>
              {availableAmenities.map((amenity) => (
                <label key={amenity}>
                  <input
                    type="checkbox"
                    name="amenities"
                    value={amenity}
                    checked={stadiumData.amenities.includes(amenity)}
                    onChange={handleAmenitiesChange}
                  />
                  {amenity}
                </label>
              ))}
            </fieldset>
            <div className="form-group">
              <label>Type:</label>
              <select name="type" value={stadiumData.type} onChange={handleChange}>
                <option value="">Select Type</option>
                {availableTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Surface:</label>
              <select name="surface" value={stadiumData.surface} onChange={handleChange}>
                <option value="">Select Surface</option>
                {availableSurfaces.map((surface) => (
                  <option key={surface} value={surface}>{surface}</option>
                ))}
              </select>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="manage-stadium-container">
      {(loadingStadiums || isSubmitting || isDeletingStadium) && (
        <LoadingOverlay
          label={
            isDeletingStadium
              ? 'Deleting stadium'
              : isSubmitting
                ? mode === 'edit'
                  ? 'Updating stadium'
                  : 'Creating stadium'
                : 'Loading stadiums'
          }
        />
      )}
      {view === 'list' ? (
        <>
          <h2>Stadiums</h2>
          {renderStadiumList()}
        </>
      ) : (
        <>
          <h2>{mode === 'edit' ? 'Modify Stadium' : 'Add New Stadium'}</h2>
          <div className="stadium-form-header-actions">
            <Button
              text="Back to Stadiums"
              onClick={handleBackToList}
              disabled={loadingStadiums || isSubmitting || isDeletingStadium}
            />
            {mode === 'edit' && selectedStadiumId && (
              <Button
                text={isDeletingStadium ? 'Deleting...' : 'Delete Stadium'}
                onClick={handleDeleteStadium}
                disabled={loadingStadiums || isSubmitting || isDeletingStadium}
              />
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {renderStepContent()}
            <div className="form-navigation">
              {step > 1 && (
                <Button
                  text="Back"
                  onClick={() => setStep(step - 1)}
                  disabled={loadingStadiums || isSubmitting || isDeletingStadium}
                />
              )}
              {step < 3 && (
                <Button
                  text="Next"
                  onClick={() => setStep(step + 1)}
                  disabled={loadingStadiums || isSubmitting || isDeletingStadium}
                />
              )}
              {step === 3 && (
                <Button
                  text={
                    isSubmitting
                      ? mode === 'edit'
                        ? 'Updating...'
                        : 'Adding...'
                      : mode === 'edit'
                        ? 'Update Stadium'
                        : 'Add Stadium'
                  }
                  type="submit"
                  disabled={loadingStadiums || isSubmitting || isDeletingStadium}
                />
              )}
            </div>
          </form>
        </>
      )}
    </div>
  );
}

export default ManageStadium;
